declare global {
  interface Window {
    ym?: (counterId: number, action: string, ...args: unknown[]) => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/**
 * Возвращает текущий вариант A/B тестирования:
 * 'A' - стандартная регистрация (форма с ФИО, email, паролем)
 * 'B' - упрощенный флоу (вход по номеру, данные обновляются позже)
 */
export const getAbVariant = (): "A" | "B" => {
  if (typeof window !== "undefined") {
    if (window.SkAuthConfig?.abAuthVariant === "B") return "B";
    if (window.SkAuthConfig?.abAuthVariant === "A") return "A";

    // Fallback: проверка cookie sk_auth_ab_variant
    const match = document.cookie.match(/(?:^|;\s*)sk_auth_ab_variant=([AB])/);
    if (match && (match[1] === "A" || match[1] === "B")) {
      return match[1];
    }
  }
  return "A";
};

/**
 * Проверяет, активен ли режим пропуска подтверждения номера для тестов:
 * через SkAuthConfig, URL параметр (?skip_otp=1 или ?skip_phone_verification=1) или cookie
 */
export const isSkipPhoneVerification = (): boolean => {
  if (typeof window !== "undefined") {
    if (window.SkAuthConfig?.skipPhoneVerification) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.has("skip_phone_verification") || params.has("skip_otp"))
      return true;
    const match = document.cookie.match(
      /(?:^|;\s*)sk_auth_skip_phone_verification=([Yy1])/,
    );
    if (match) return true;
  }
  return false;
};

/**
 * Отправляет цель и параметры эксперимента в Яндекс.Метрику и dataLayer.
 * При наличии callback вызывает его после отправки хита в Метрику или по страховочному таймауту.
 */
export const trackAuthGoal = (
  goalName: string,
  params?: Record<string, unknown>,
  callback?: () => void,
): void => {
  if (typeof window === "undefined") {
    callback?.();
    return;
  }

  let callbackCalled = false;
  const safeCallback = () => {
    if (!callbackCalled) {
      callbackCalled = true;
      callback?.();
    }
  };

  // Страховочный таймаут (на случай блокировки Метрики AdBlock или сбоя сети)
  if (callback) {
    setTimeout(safeCallback, 350);
  }

  try {
    const counterId = window.SkAuthConfig?.metrikaCounterId || 96536298;
    const abVariant = getAbVariant();
    const payload = {
      ab_variant: abVariant,
      ...params,
    };

    // 1. Отправка цели и визиторских параметров в Яндекс.Метрику
    if (typeof window.ym === "function") {
      window.ym(counterId, "reachGoal", goalName, payload, safeCallback);
      window.ym(counterId, "params", { ab_auth_variant: abVariant });
    } else {
      safeCallback();
    }

    // 2. dataLayer (GTM / ecommerce)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: goalName,
      ...payload,
    });

    console.log(`[SkAuth Metrika] Goal reached: ${goalName}`, payload);
  } catch (error) {
    console.error("[SkAuth Metrika] Error tracking goal:", error);
    safeCallback();
  }
};
